/**
 * The owner's vault (« Coffre »): items added in the app's form (or through the one-tap link the agent sends), imported
 * from a password-manager CSV export, received through a 1Password share link, pasted in a message, or created by the
 * agent when it opens an account (generated password). Passwords, cards, bank details, identities, notes. Values live
 * only encrypted in the agent's Durable Object and in the fill helper of the cloud computer: the model, transcripts,
 * memory, logs and telemetry only ever see the views below (labels and field kinds); the owner sees masked hints and
 * reveals a value only after a fresh re-authentication. Pure and environment-neutral (Worker, web client, E2E).
 */

import { APP_PASSWORD_PLACEHOLDER } from "./app-password";

export const VAULT_ITEM_KINDS = ["login", "card", "bank", "identity", "note", "password", "other"] as const;
export type VaultItemKind = (typeof VAULT_ITEM_KINDS)[number];

export const FIELD_KINDS = [
  "username",
  "email",
  "password",
  "otp",
  "card_holder",
  "card_number",
  "card_expiry",
  "card_cvc",
  "card_pin",
  "bank_holder",
  "iban",
  "bic",
  "account_number",
  "routing_number",
  "full_name",
  "first_name",
  "last_name",
  "phone",
  "address",
  "city",
  "postal_code",
  "country",
  "birth_date",
  "note",
  "other",
] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export function isFieldKind(v: unknown): v is FieldKind {
  return typeof v === "string" && (FIELD_KINDS as readonly string[]).includes(v);
}

/** Field kinds a fill can type into a web form, per item kind (notes and « other » fields are never filled). */
export const FILLABLE: Record<VaultItemKind, readonly FieldKind[]> = {
  // « otp » holds a TOTP secret: the fill types the CURRENT code, generated server side, never the secret.
  login: ["username", "email", "password", "otp"],
  password: ["username", "email", "password", "otp"],
  // A card's billing address goes with it on a checkout form.
  card: ["card_holder", "card_number", "card_expiry", "card_cvc", "address", "city", "postal_code", "country"],
  bank: ["bank_holder", "iban", "bic", "account_number", "routing_number"],
  identity: ["full_name", "first_name", "last_name", "email", "phone", "address", "city", "postal_code", "country", "birth_date"],
  note: [],
  other: [],
};

export const FIELD_LABEL_FR: Record<FieldKind, string> = {
  username: "identifiant",
  email: "e-mail",
  password: "mot de passe",
  otp: "double authentification",
  card_holder: "titulaire",
  card_number: "numéro de carte",
  card_expiry: "date d'expiration",
  card_cvc: "cryptogramme",
  card_pin: "code PIN",
  bank_holder: "titulaire du compte",
  iban: "IBAN",
  bic: "BIC",
  account_number: "numéro de compte",
  routing_number: "code banque",
  full_name: "nom complet",
  first_name: "prénom",
  last_name: "nom",
  phone: "téléphone",
  address: "adresse",
  city: "ville",
  postal_code: "code postal",
  country: "pays",
  birth_date: "date de naissance",
  note: "note",
  other: "autre champ",
};

export const ITEM_KIND_FR: Record<VaultItemKind, string> = {
  login: "Identifiant",
  password: "Mot de passe",
  card: "Carte bancaire",
  bank: "Compte bancaire",
  identity: "Identité",
  note: "Note sécurisée",
  other: "Élément",
};

export type VaultItemStatus = "opening" | "ready" | "failed";

/** Where an item came from. */
export const VAULT_SOURCES = ["link", "paste", "form", "import", "generated"] as const;
export type VaultSource = (typeof VAULT_SOURCES)[number];

export const VAULT_SOURCE_FR: Record<VaultSource, string> = {
  link: "lien 1Password",
  paste: "collé dans un message",
  form: "ajouté dans l'app",
  import: "importé (fichier CSV)",
  generated: "créé par l'agent (mot de passe généré)",
};

/** Who used an item (vault_uses.actor): the main agent, a mission on the cloud computer, an extra agent. */
export interface VaultUseView {
  at: number;
  site: string | null;
  /** « l'agent », « une mission », « l'agent Atlas ». */
  by: string;
  /** login, payment, identity, bank, otp, signup. */
  what: VaultUseKind;
}
export type VaultUseKind = "login" | "otp" | "payment" | "bank" | "identity" | "signup";

export const VAULT_USE_FR: Record<VaultUseKind, string> = {
  login: "connexion",
  otp: "code de double authentification",
  payment: "paiement",
  bank: "coordonnées bancaires",
  identity: "coordonnées",
  signup: "création de compte",
};

/** Why a link could not be opened (never a value). */
export const VAULT_PROBLEMS = [
  "link_unavailable",
  "email_verification_required",
  "share_page_unrecognized",
  "unsupported_item",
  "computer_unavailable",
  "unverified_sender",
  "keyring_unavailable",
  "timeout",
  "internal",
] as const;
export type VaultProblem = (typeof VAULT_PROBLEMS)[number];

export const VAULT_PROBLEM_FR: Record<VaultProblem, string> = {
  link_unavailable: "le lien a expiré, a été supprimé ou a déjà été consulté",
  email_verification_required: "le lien est réservé à certaines adresses e-mail (code envoyé par e-mail)",
  share_page_unrecognized: "la page 1Password n'a pas pu être lue",
  unsupported_item: "ce type d'élément n'est pas pris en charge (document, fichier)",
  computer_unavailable: "l'ordinateur cloud n'a pas pu l'ouvrir",
  unverified_sender:
    "il est arrivé par SMS ou par e-mail (expéditeur non vérifié) alors que ton ordinateur cloud était éteint : renvoie-le par iMessage, Telegram ou dans l'app",
  keyring_unavailable: "le chiffrement est indisponible",
  timeout: "l'ouverture a pris trop de temps",
  internal: "erreur interne",
};

/** One vault item as the web app and the API show it (never a value). */
export interface VaultItemView {
  n: number;
  kind: VaultItemKind;
  label: string;
  /** Site the item belongs to (origin or host), when known. Metadata, not a secret. */
  website: string | null;
  /** Field kinds the item holds. */
  fields: FieldKind[];
  source: VaultSource;
  channel: string;
  status: VaultItemStatus;
  /** Masked previews for the owner (« •••• 1111 », « ro•••@example.com »): never a whole value. */
  hints: Partial<Record<FieldKind, string>>;
  /** The latest use, when any. */
  lastUse: VaultUseView | null;
  problem: VaultProblem | null;
  /** Expiry of the 1Password link, as the share page showed it (null when unknown or pasted). */
  linkExpiresAt: number | null;
  uses: number;
  lastUsedAt: number | null;
  createdAt: number;
}

/** « Identifiant Netflix (identifiant + mot de passe) »: the line the model and the app show for an item. */
export function describeItem(item: Pick<VaultItemView, "kind" | "label" | "fields">): string {
  const kinds = item.fields.filter((k) => k !== "other");
  const what = kinds.length ? ` (${[...new Set(kinds.map((k) => FIELD_LABEL_FR[k]))].join(" + ")})` : "";
  return `${ITEM_KIND_FR[item.kind]} ${item.label}${what}`;
}

// ───────────── fills ─────────────

/** Every outcome of a fill (codes only, never a value). */
export const FILL_CODES = [
  "ok",
  "partial",
  "no_target_tab",
  "origin_mismatch",
  "no_matching_form",
  "browser_not_running",
  "computer_not_running",
  // The owner holds the controls of the screen (/ordinateur « Prendre les commandes »): nothing is typed.
  "user_in_control",
  "item_not_ready",
  "nothing_to_fill",
  "timeout",
  "bad_request",
  // The fill helper on the computer is not the one Pulsez shipped (machine/vault-exec.ts): nothing was typed.
  "integrity",
  "internal",
] as const;
export type FillCode = (typeof FILL_CODES)[number];

/** What the fill helper answers: codes and field kinds only. */
export interface FillOutcome {
  code: FillCode;
  origin?: string;
  filled?: FieldKind[];
  missing?: FieldKind[];
  submitted?: boolean;
  /**
   * Nothing typed and: frames of another site hold the fields but could not be read (payment_frame), or the fields are
   * in frames of an origin the vault never types into (frame_refused: not a known payment provider, or embedded through
   * an unknown site).
   */
  hint?: "payment_frame" | "frame_refused";
  /** Payment providers whose frames received card fields (« Stripe »…). */
  providers?: string[];
  /** Origins of frames showing card or sign-in fields that were refused. */
  refused?: string[];
}

/** One value handed to the fill helper (worker → container stdin only). */
export interface FillValue {
  kind: FieldKind;
  value: string;
  /** A two-factor code only: the code of the next period, typed instead once the current one has expired. */
  next?: string;
  /** A two-factor code only: how long (ms) the current code stays valid from now. */
  validMs?: number;
}

/** What the extraction helper answers (worker side only: values cross the RPC once, then are sealed). */
export interface ExtractedItem {
  category: string;
  title: string;
  website: string | null;
  fields: { kind: FieldKind; label: string; value: string }[];
}

/** `URL.origin` (lower-case, punycode) of an http(s) address without credentials; null otherwise. */
export function parseOrigin(raw: string): string | null {
  const text = raw.trim();
  if (!text || text.length > 300) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
  if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password || !url.hostname) return null;
  return url.origin;
}

/**
 * Hosting suffixes under which every customer gets its own subdomain (the public suffix list's « private » section,
 * the common ones): « owner.github.io » and « attacker.github.io » are two different sites. Mirrored in
 * container/secure-fill.mjs (SHARED_SUFFIXES): keep both lists identical.
 */
export const SHARED_SUFFIXES: readonly string[] = [
  "github.io", "gitlab.io", "bitbucket.io", "codeberg.page", "pages.dev", "workers.dev", "r2.dev", "trycloudflare.com",
  "vercel.app", "now.sh", "netlify.app", "netlify.com", "herokuapp.com", "herokudns.com", "web.app", "firebaseapp.com",
  "appspot.com", "run.app", "cloudfunctions.net", "azurewebsites.net", "azurestaticapps.net", "cloudapp.net",
  "cloudfront.net", "amplifyapp.com", "elasticbeanstalk.com", "s3.amazonaws.com", "ondigitalocean.app",
  "digitaloceanspaces.com", "onrender.com", "fly.dev", "railway.app", "up.railway.app", "deno.dev", "glitch.me",
  "repl.co", "replit.app", "replit.dev", "surge.sh", "neocities.org", "myshopify.com", "wixsite.com", "squarespace.com",
  "webflow.io", "framer.app", "framer.website", "carrd.co", "notion.site", "blogspot.com", "wordpress.com", "tumblr.com",
  "weebly.com", "readthedocs.io", "hf.space", "streamlit.app", "lovable.app", "ngrok.io", "ngrok.app", "ngrok-free.app",
  "loca.lt", "translate.goog", "gitbook.io", "my.canva.site", "sharepoint.com", "zendesk.com", "freshdesk.com",
  "atlassian.net", "myjetbrains.com", "bubbleapps.io", "softr.app", "glide.page", "ghost.io", "substack.com",
];

/** Second-level labels of country-code domains that are themselves public suffixes (« co.uk », « com.au », « gouv.fr »). */
const CC_SECOND_LEVEL = /^(?:co|com|org|net|gov|gouv|ac|edu|ne|or|go|gv|nom|asso|ltd|plc|me|mil|sch|nic|gob|gen|biz|info|in|ind|firm)\.[a-z]{2}$/;

/** The registrable part of a host: one label under its public suffix (shared hosting suffixes included). */
export function siteOf(origin: string): string {
  let host: string;
  try {
    host = new URL(origin).hostname.toLowerCase();
  } catch {
    host = origin.toLowerCase();
  }
  host = host.replace(/\.$/, "");
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(host) || host.includes(":")) return host;
  const labels = host.split(".").filter(Boolean);
  for (let k = labels.length - 1; k >= 1; k--) {
    // The longest shared suffix the host ends with (« up.railway.app » before « railway.app »).
    const suffix = labels.slice(-k).join(".");
    if (SHARED_SUFFIXES.includes(suffix)) return labels.length > k ? labels.slice(-(k + 1)).join(".") : suffix;
  }
  const two = labels.slice(-2).join(".");
  return CC_SECOND_LEVEL.test(two) && labels.length >= 3 ? labels.slice(-3).join(".") : two;
}

/** True when the page origin belongs to the item's site (same registrable domain). */
export function sameSite(itemWebsite: string | null, pageOrigin: string): boolean {
  if (!itemWebsite) return false;
  const a = parseOrigin(itemWebsite);
  return a !== null && siteOf(a) === siteOf(pageOrigin);
}

// ───────────── values pasted in a message ─────────────

/** One value found in a message, with the item it belongs to (`item` indexes PastedSecrets.items). */
export interface PastedSpan {
  start: number;
  end: number;
  item: number;
  kind: FieldKind;
}

export interface PastedItem {
  kind: VaultItemKind;
  label: string | null;
  website: string | null;
  fields: { kind: FieldKind; value: string }[];
}

export interface PastedSecrets {
  items: PastedItem[];
  spans: PastedSpan[];
}

const MAX_SCAN = 16_384;
const CARD = /(?<![\d])(?:\d[ -]?){12,18}\d(?![\d])/g;
const IBAN = /\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]){11,30}\b/gi;
const EXPIRY = /\b(?:exp(?:ire|iration|\.)?|valid(?:e|ité)?(?: jusqu'?(?:au|à))?|date d'expiration|expiry|expires?)?\s*[:=]?\s*\b(0[1-9]|1[0-2])\s?[/-]\s?(\d{4}|\d{2})\b/giu;
const CVC = /\b(?:cvc|cvv|cvv2|crypto(?:gramme)?|code de s[ée]curit[ée]|security code)\b\s*(?:[:=]|est|is)?\s*(\d{3,4})\b/giu;
const BIC = /\b(?:[bB][iI][cC]|[sS][wW][iI][fF][tT])\b\s*(?:[:=]|est|is)?\s*([A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?)\b/gu;
/** A password keyword, not a part of a path or an address (« file:///etc/passwd »). */
const PASSWORD_KEYWORD = /(?<![/\\.\w-])(?:mots? de passe|mdp|password|passwd|pwd|passcode|code secret)\b(?![/\\.]\w)/giu;
const PASSWORD_WORD = new RegExp(`^${PASSWORD_KEYWORD.source}$`, "iu");
const USERNAME_KEYWORD = /\b(?:identifiant|login|username|user ?name|nom d'utilisateur|e-?mail|adresse (?:e-?)?mail)\b\s*(?:[:=]|est|is|c'est)\s*["'«“]?([^\s"'»”,;]{3,120})/giu;
const DOMAIN = /(?<![@\w.-])((?:[a-z0-9-]+\.)+(?:com|fr|net|org|io|co|app|be|ch|ca|de|es|it|uk|eu|tv|me|ai))\b/giu;
/** Words that say a number is a card. */
const CARD_WORDS = /\b(?:cartes?|card|cb|visa|master ?card|amex|american express|maestro|discover|jcb|carte bleue|cr[ée]dit|d[ée]bit)\b/iu;
/** Words that say a Luhn-valid number is something else (company numbers, phone identifiers, parcels). */
const NOT_CARD_WORDS = /\b(?:siret|siren|imei|iccid|tva|vat|suivi|tracking|colis|parcel|n° de s[ée]rie|serial)\b/iu;
/** Words that follow « mot de passe : » without being one. */
const NOT_A_PASSWORD = new Set(
  "oublié oublie perdu perdue changé change incorrect incorrecte faux fausse erroné reset réinitialisé réinitialiser expiré bloqué bloque refusé invalide wrong forgot forgotten lost expired invalid changed".split(" "),
);
/** Words of a sentence, not of a passphrase (« mdp : soleil tu peux te connecter »). */
const PROSE_WORDS = new Set(
  "et ou mais donc puis merci stp svp tu te je j il elle on nous vous ils me moi toi se ne pas peux peut veux est sont a as ai pour sur dans avec chez de du des le la les un une au aux en y ce cette ça ca si que qui quand and or but then thanks please you your i it is are to for on in with the a an of my me can".split(
    " ",
  ),
);
const OPEN_QUOTES: Record<string, string> = { '"': '"', "'": "'", "«": "»", "“": "”", "‘": "’" };

function luhn(digits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = digits.charCodeAt(i) - 48;
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/** True when the digits are a card number of a known network (issuer prefix and length). */
function cardNetwork(d: string): boolean {
  const n = d.length;
  const p2 = Number(d.slice(0, 2));
  const p3 = Number(d.slice(0, 3));
  const p4 = Number(d.slice(0, 4));
  const p6 = Number(d.slice(0, 6));
  if (d[0] === "4") return n === 13 || n === 16 || n === 19; // Visa
  if ((p2 >= 51 && p2 <= 55) || (p4 >= 2221 && p4 <= 2720)) return n === 16; // Mastercard
  if (p4 >= 2200 && p4 <= 2204) return n === 16; // Mir
  if (p2 === 34 || p2 === 37) return n === 15; // American Express
  if (p4 === 6011 || p2 === 65 || (p3 >= 644 && p3 <= 649) || (p6 >= 622126 && p6 <= 622925)) return n >= 16 && n <= 19; // Discover
  if (p2 === 62) return n >= 16 && n <= 19; // UnionPay
  if (p4 >= 3528 && p4 <= 3589) return n >= 16 && n <= 19; // JCB
  if (p2 === 36 || p2 === 38 || p2 === 39 || (p3 >= 300 && p3 <= 305)) return n >= 14 && n <= 19; // Diners Club
  if ([5018, 5020, 5038, 5893, 6304, 6759, 6761, 6762, 6763].includes(p4)) return n >= 13 && n <= 19; // Maestro
  return false;
}

function ibanValid(raw: string): boolean {
  const s = raw.replace(/\s+/g, "").toUpperCase();
  if (s.length < 15 || s.length > 34 || !/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(s)) return false;
  const moved = s.slice(4) + s.slice(0, 4);
  let rem = 0;
  for (const ch of moved) {
    const code = ch.charCodeAt(0);
    const val = code >= 65 ? String(code - 55) : ch;
    for (const d of val) rem = (rem * 10 + (d.charCodeAt(0) - 48)) % 97;
  }
  return rem === 1;
}

function overlaps(spans: PastedSpan[], start: number, end: number): boolean {
  return spans.some((s) => start < s.end && end > s.start);
}

/** A short label from the words between a keyword and its value (« mot de passe Netflix : … » → « Netflix »). */
function labelBetween(words: string): string | null {
  const cleaned = words
    .replace(/[:=]/g, " ")
    .replace(/\b(?:de|du|des|d'|pour|for|of|my|mon|ma|mes|le|la|les|l'|sur|on|est|is|c'est)\b/giu, " ")
    .replace(/[()«»"“”]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return /\p{L}{2,}/u.test(cleaned) ? cleaned.slice(0, 40) : null;
}

/** First match of a global pattern whose value starts inside [from, to). */
function firstIn(re: RegExp, text: string, from: number, to: number): RegExpMatchArray | null {
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0;
    if (at >= from && at < to) return m;
    if (at >= to) break;
  }
  return null;
}

/** The first domain name written inside [from, to). */
function domainIn(text: string, from: number, to: number): string | null {
  return firstIn(DOMAIN, text, from, to)?.[1]?.toLowerCase() ?? null;
}

/**
 * The password written at `pos` (just after its separator). Quoted values are read up to the matching quote; others
 * up to the next whitespace, quotes and « ! ? » included (passwords end with them), a trailing « , ; . » being the
 * sentence's. After an explicit « : » / « = », a line of plain lower-case words (« correct horse battery staple ») is
 * one passphrase.
 */
function readPassword(text: string, pos: number, explicit: boolean): { start: number; end: number } | null {
  while (pos < text.length && (text[pos] === " " || text[pos] === "\t")) pos++;
  const close = OPEN_QUOTES[text[pos] ?? ""];
  if (close) {
    const line = text.slice(pos + 1, pos + 1 + 130).split("\n")[0]!;
    const at = line.indexOf(close);
    if (at >= 4 && at <= 128) return { start: pos + 1, end: pos + 1 + at };
  }
  const token = /^\S{1,128}/u.exec(text.slice(pos, pos + 128))?.[0] ?? "";
  if (!token) return null;
  let end = pos + token.length;
  // Sentence punctuation and an unbalanced closing bracket or quote are not part of the value.
  for (;;) {
    const value = text.slice(pos, end);
    const last = value.slice(-1);
    if (/[,;]/.test(last) || (last === "." && !value.slice(0, -1).endsWith("."))) end--;
    else if ((last === ")" && !value.includes("(")) || (last === "]" && !value.includes("[")) || ((last === "»" || last === "”") && !/[«“]/.test(value))) end--;
    else break;
    if (end <= pos) return null;
  }
  if (explicit && /^\p{Ll}+$/u.test(token)) {
    // « mot de passe : correct horse battery staple »: the rest of the line, when it is only plain words.
    const rest = /^((?: \p{Ll}+){2,7})[.!]?[ \t]*(?:\n|$)/u.exec(text.slice(pos + token.length, pos + 200));
    if (rest && !rest[1]!.trim().split(" ").some((w) => PROSE_WORDS.has(w) || PASSWORD_WORD.test(w))) end = pos + token.length + rest[1]!.length;
  }
  return { start: pos, end };
}

/**
 * Values the owner pasted in a message (card numbers with their expiry and security code, IBAN/BIC, passwords
 * announced by a keyword, the username next to them) — every one of them, not only the first. Deterministic; returns
 * spans to replace and the items to keep.
 */
export function findPastedSecrets(raw: string): PastedSecrets {
  const text = raw.length > MAX_SCAN ? raw.slice(0, MAX_SCAN) : raw;
  const items: PastedItem[] = [];
  const spans: PastedSpan[] = [];
  const firstDomain = domainIn(text, 0, text.length);

  // Bank details: every valid IBAN, each with the BIC written after it (before the next IBAN).
  const ibans: { start: number; end: number; iban: string }[] = [];
  const ibanRe = new RegExp(IBAN.source, IBAN.flags);
  for (let m = ibanRe.exec(text); m; m = ibanRe.exec(text)) {
    // The pattern may run into the next words (« … 189 BIC AGRIFRPP », « … 189 et DE89 … »): the longest valid
    // prefix, cut at a space; the scan then resumes right after it.
    const parts = m[0].split(" ");
    let candidate = "";
    for (let k = parts.length; k >= 1 && !candidate; k--) {
      const c = parts.slice(0, k).join(" ");
      if (ibanValid(c)) candidate = c;
    }
    const start = m.index;
    ibanRe.lastIndex = start + (candidate || parts[0]!).length;
    if (candidate) ibans.push({ start, end: start + candidate.length, iban: candidate.replace(/\s+/g, "").toUpperCase() });
  }
  ibans.forEach((b, i) => {
    if (overlaps(spans, b.start, b.end)) return;
    const item = items.push({ kind: "bank", label: `IBAN ${b.iban.slice(0, 4)}…${b.iban.slice(-4)}`, website: null, fields: [{ kind: "iban", value: b.iban }] }) - 1;
    spans.push({ start: b.start, end: b.end, item, kind: "iban" });
    const bic = firstIn(BIC, text, ibans.length === 1 ? 0 : b.end, ibans[i + 1]?.start ?? text.length);
    if (bic) {
      const s = (bic.index ?? 0) + bic[0].lastIndexOf(bic[1]!);
      if (!overlaps(spans, s, s + bic[1]!.length)) {
        items[item]!.fields.push({ kind: "bic", value: bic[1]! });
        spans.push({ start: s, end: s + bic[1]!.length, item, kind: "bic" });
      }
    }
  });

  // Cards: a Luhn-valid number of a known network, said to be a card (a word, an expiry, a security code, or written
  // in groups of four), and not a company or device number; each with the expiry and code written near it.
  const cards: { start: number; end: number; digits: string }[] = [];
  const isCard = (raw: string, start: number): boolean => {
    const digits = raw.replace(/\D/g, "");
    const end = start + raw.length;
    if (digits.length < 13 || digits.length > 19 || /^(\d)\1+$/.test(digits) || !luhn(digits) || !cardNetwork(digits)) return false;
    if (overlaps(spans, start, end)) return false;
    if (NOT_CARD_WORDS.test(text.slice(Math.max(0, start - 50), start))) return false;
    const near = text.slice(Math.max(0, start - 80), Math.min(text.length, end + 80));
    const grouped = /^\d{4}([ -])\d{4}\1\d{4}\1\d{1,7}$/.test(raw) || /^\d{4} \d{6} \d{4,5}$/.test(raw);
    const expiry = new RegExp(EXPIRY.source, "iu").test(text.slice(end, Math.min(text.length, end + 60)));
    return CARD_WORDS.test(near) || grouped || new RegExp(CVC.source, "iu").test(near) || expiry;
  };
  for (const m of text.matchAll(CARD)) {
    // The run may take in a neighbouring number (« 4111 1111 1111 1111 12/29 »): its sub-runs at the separators,
    // longest first.
    const at = m.index ?? 0;
    const cuts = [0, ...[...m[0].matchAll(/[ -]/g)].map((x) => (x.index ?? 0) + 1)];
    const ends = [...[...m[0].matchAll(/[ -]/g)].map((x) => x.index ?? 0), m[0].length];
    const ranges: [number, number][] = [];
    for (const a of cuts) for (const b of ends) if (b > a) ranges.push([a, b]);
    ranges.sort((x, y) => y[1] - y[0] - (x[1] - x[0]));
    const hit = ranges.find(([a, b]) => isCard(m[0].slice(a, b), at + a));
    if (hit) cards.push({ start: at + hit[0], end: at + hit[1], digits: m[0].slice(hit[0], hit[1]).replace(/\D/g, "") });
  }
  cards.forEach((c, i) => {
    const item = items.push({ kind: "card", label: `•••• ${c.digits.slice(-4)}`, website: null, fields: [{ kind: "card_number", value: c.digits }] }) - 1;
    spans.push({ start: c.start, end: c.end, item, kind: "card_number" });
    // Its expiry and code: after it (up to the next card), else before it (back to the previous card).
    const after: [number, number] = [c.end, cards[i + 1]?.start ?? text.length];
    const before: [number, number] = [cards[i - 1]?.end ?? 0, c.start];
    for (const [re, kind] of [
      [EXPIRY, "card_expiry"],
      [CVC, "card_cvc"],
    ] as const) {
      for (const [from, to] of [after, before]) {
        let found = false;
        for (const e of text.matchAll(re)) {
          const at = e.index ?? 0;
          if (at < from) continue;
          if (at >= to) break;
          const s = kind === "card_expiry" ? at + e[0].indexOf(e[1]!) : at + e[0].lastIndexOf(e[1]!);
          const valueEnd = kind === "card_expiry" ? at + e[0].length : s + e[1]!.length;
          if (overlaps(spans, s, valueEnd)) continue;
          const value = kind === "card_expiry" ? `${e[1]}/${e[2]!.length === 4 ? e[2]!.slice(2) : e[2]}` : e[1]!;
          items[item]!.fields.push({ kind, value });
          spans.push({ start: s, end: valueEnd, item, kind });
          found = true;
          break;
        }
        if (found) break;
      }
    }
  });

  // Passwords: a keyword, a few words at most (the label), an explicit separator (or « est »), then the value; every
  // one of them, each with the username and site written in its own part of the message.
  const keywords = [...text.matchAll(PASSWORD_KEYWORD)];
  let prevEnd = 0;
  keywords.forEach((m, i) => {
    const after = (m.index ?? 0) + m[0].length;
    if (overlaps(spans, m.index ?? 0, after)) return;
    const tail = text.slice(after, after + 160);
    // « https:// » is an address, not a « : » separator.
    const v = /^((?:\s+[^\s:=]{1,30}){0,3}?)\s*([:=](?!\/\/)|\best\b|\bis\b|\bc'est\b)/u.exec(tail);
    if (!v) return;
    const explicit = v[2] === ":" || v[2] === "=";
    const at = readPassword(text, after + v[0].length, explicit);
    if (!at) return;
    // An Apple app-specific password (abcd-efgh-ijkl-mnop) is iCloud's, never the vault's (inbound.ts runs the iCloud
    // capture first): neither the value nor the placeholder that already masks it.
    if (text.startsWith(APP_PASSWORD_PLACEHOLDER, at.start) || /^[a-z]{4}-[a-z]{4}-[a-z]{4}-[a-z]{4}$/i.test(text.slice(at.start, at.end))) return;
    const value = text.slice(at.start, at.end);
    if (value.length < 4 || NOT_A_PASSWORD.has(value.toLowerCase())) return;
    // « mon mot de passe est trop long »: after « est », only a value that does not look like a word.
    if (!explicit && !/\d|[^\p{L}'’-]|[a-z].*[A-Z]/u.test(value)) return;
    if (overlaps(spans, at.start, at.end)) return;
    const segEnd = keywords[i + 1]?.index ?? text.length;
    const domain = domainIn(text, prevEnd, segEnd) ?? (keywords.length === 1 ? firstDomain : null);
    const label = labelBetween(v[1] ?? "") ?? domain;
    const item = items.push({ kind: "login", label, website: domain ? `https://${domain}` : null, fields: [{ kind: "password", value }] }) - 1;
    spans.push({ start: at.start, end: at.end, item, kind: "password" });
    // Its username: written before its keyword (since the previous password), else after its value.
    for (const [from, to] of [
      [prevEnd, m.index ?? 0],
      [at.end, segEnd],
    ] as const) {
      const u = [...text.matchAll(USERNAME_KEYWORD)].find((x) => {
        const s = (x.index ?? 0) + x[0].lastIndexOf(x[1]!);
        return s >= from && s < to && !overlaps(spans, s, s + x[1]!.length);
      });
      if (!u) continue;
      const s = (u.index ?? 0) + u[0].lastIndexOf(u[1]!);
      const isEmail = /@/.test(u[1]!);
      items[item]!.fields.unshift({ kind: isEmail ? "email" : "username", value: u[1]! });
      spans.push({ start: s, end: s + u[1]!.length, item, kind: isEmail ? "email" : "username" });
      break;
    }
    prevEnd = at.end;
  });
  spans.sort((a, b) => a.start - b.start);
  return { items, spans };
}

// ───────────── the « Ajouter » form, the one-tap link, the CSV import ─────────────

/** The kinds the app's form adds. */
export const FORM_KINDS = ["login", "card", "identity", "bank", "note"] as const;
export type FormKind = (typeof FORM_KINDS)[number];

export const FORM_KIND_FR: Record<FormKind, string> = {
  login: "Identifiant",
  card: "Carte bancaire",
  identity: "Identité / adresse",
  bank: "IBAN",
  note: "Note sécurisée",
};

export interface FormFieldSpec {
  kind: FieldKind;
  label: string;
  required?: boolean;
  /** A value hidden while typed (type=password). */
  secret?: boolean;
  placeholder?: string;
  autocomplete?: string;
  multiline?: boolean;
  inputMode?: "numeric" | "text" | "email" | "tel";
}

/** The fields of each kind of the form (the site and the label are separate inputs). */
export const FORM_FIELDS: Record<FormKind, readonly FormFieldSpec[]> = {
  login: [
    { kind: "username", label: "Identifiant ou e-mail", autocomplete: "off", placeholder: "toi@exemple.fr" },
    { kind: "password", label: "Mot de passe", required: true, secret: true, autocomplete: "new-password" },
    { kind: "otp", label: "Clé de double authentification (TOTP, facultatif)", secret: true, autocomplete: "off", placeholder: "JBSW Y3DP EHPK 3PXP ou otpauth://…" },
  ],
  card: [
    { kind: "card_holder", label: "Titulaire", autocomplete: "off" },
    { kind: "card_number", label: "Numéro de carte", required: true, secret: true, autocomplete: "off", inputMode: "numeric" },
    { kind: "card_expiry", label: "Expiration (MM/AA)", required: true, autocomplete: "off", placeholder: "12/29", inputMode: "numeric" },
    { kind: "card_cvc", label: "Cryptogramme", secret: true, autocomplete: "off", inputMode: "numeric" },
    { kind: "address", label: "Adresse de facturation", autocomplete: "off" },
    { kind: "postal_code", label: "Code postal", autocomplete: "off" },
    { kind: "city", label: "Ville", autocomplete: "off" },
    { kind: "country", label: "Pays", autocomplete: "off" },
  ],
  identity: [
    { kind: "first_name", label: "Prénom", autocomplete: "off" },
    { kind: "last_name", label: "Nom", autocomplete: "off" },
    { kind: "email", label: "E-mail", autocomplete: "off", inputMode: "email" },
    { kind: "phone", label: "Téléphone", autocomplete: "off", inputMode: "tel" },
    { kind: "address", label: "Adresse", autocomplete: "off" },
    { kind: "postal_code", label: "Code postal", autocomplete: "off" },
    { kind: "city", label: "Ville", autocomplete: "off" },
    { kind: "country", label: "Pays", autocomplete: "off" },
    { kind: "birth_date", label: "Date de naissance", autocomplete: "off", placeholder: "JJ/MM/AAAA" },
  ],
  bank: [
    { kind: "bank_holder", label: "Titulaire du compte", autocomplete: "off" },
    { kind: "iban", label: "IBAN", required: true, secret: true, autocomplete: "off" },
    { kind: "bic", label: "BIC", autocomplete: "off" },
  ],
  note: [{ kind: "note", label: "Note", required: true, secret: true, multiline: true, autocomplete: "off" }],
};

/** What the form (or the one-tap link page) sends. */
export interface VaultAddInput {
  kind: FormKind;
  label?: string | null;
  website?: string | null;
  fields: Partial<Record<FieldKind, string>>;
}

/** A checked item, ready to be sealed. */
export interface NormalizedItem {
  kind: VaultItemKind;
  label: string;
  website: string | null;
  fields: { kind: FieldKind; value: string }[];
}

const BASE32 = /^[A-Z2-7]+=*$/;

/** A TOTP secret as typed (base32 with spaces, or an otpauth:// URI) → its canonical form, or null when invalid. */
export function normalizeTotp(raw: string): string | null {
  const text = raw.trim();
  if (/^otpauth:\/\//i.test(text)) {
    try {
      const url = new URL(text);
      const secret = (url.searchParams.get("secret") ?? "").replace(/[\s-]/g, "").toUpperCase();
      if (url.hostname.toLowerCase() !== "totp" || !BASE32.test(secret) || secret.replace(/=+$/, "").length < 16) return null;
      return text.length <= 2_000 ? text : null;
    } catch {
      return null;
    }
  }
  const secret = text.replace(/[\s-]/g, "").toUpperCase();
  return BASE32.test(secret) && secret.replace(/=+$/, "").length >= 16 && secret.length <= 256 ? secret : null;
}

/** « 12/29 », « 12 / 2029 », « 1229 » → « 12/29 » (null when it is not a month and a year). */
export function normalizeExpiry(raw: string): string | null {
  const m = /^\s*(0?[1-9]|1[0-2])\s*[/ .-]?\s*(\d{4}|\d{2})\s*$/.exec(raw);
  if (!m) return null;
  const year = m[2]!.length === 4 ? m[2]!.slice(2) : m[2]!;
  return `${m[1]!.padStart(2, "0")}/${year}`;
}

/** Card number checks for the form (Luhn, 12–19 digits). */
export function validCardNumber(raw: string): string | null {
  const digits = raw.replace(/[\s-]/g, "");
  return /^\d{12,19}$/.test(digits) && luhn(digits) ? digits : null;
}

/** An IBAN typed with or without spaces → compact upper case, or null when its checksum is wrong. */
export function validIban(raw: string): string | null {
  const s = raw.replace(/\s+/g, "").toUpperCase();
  return ibanValid(s) ? s : null;
}

function hostLabel(website: string | null): string | null {
  if (!website) return null;
  try {
    return siteOf(website);
  } catch {
    return null;
  }
}

/**
 * Checks and normalises what the form sends (French error messages, never echoing a value). Empty optional fields are
 * dropped; the label defaults to the site, the card's last digits, the IBAN's ends.
 */
export function normalizeVaultAdd(input: VaultAddInput): { ok: true; item: NormalizedItem } | { ok: false; error: string } {
  if (!(FORM_KINDS as readonly string[]).includes(input.kind)) return { ok: false, error: "Type d'élément inconnu." };
  const specs = FORM_FIELDS[input.kind];
  const fields: { kind: FieldKind; value: string }[] = [];
  let website: string | null = null;
  if (input.website && input.website.trim()) {
    website = parseOrigin(input.website);
    if (!website) return { ok: false, error: "Adresse du site invalide (par exemple https://www.netflix.com)." };
  }
  for (const spec of specs) {
    const raw = input.fields[spec.kind];
    // A password is kept exactly as typed (its spaces may be part of it); everything else is trimmed.
    const text = typeof raw === "string" ? (spec.kind === "password" ? raw.replace(/[\r\n]+$/, "") : raw.trim()) : "";
    if (!text.trim()) {
      if (spec.required) return { ok: false, error: `${spec.label} : champ obligatoire.` };
      continue;
    }
    if (text.length > (spec.kind === "note" ? 20_000 : 500)) return { ok: false, error: `${spec.label} : trop long.` };
    let value = text;
    if (spec.kind === "card_number") {
      const v = validCardNumber(text);
      if (!v) return { ok: false, error: "Numéro de carte invalide." };
      value = v;
    } else if (spec.kind === "card_expiry") {
      const v = normalizeExpiry(text);
      if (!v) return { ok: false, error: "Date d'expiration invalide (MM/AA)." };
      value = v;
    } else if (spec.kind === "card_cvc") {
      if (!/^\d{3,4}$/.test(text)) return { ok: false, error: "Cryptogramme invalide (3 ou 4 chiffres)." };
    } else if (spec.kind === "iban") {
      const v = validIban(text);
      if (!v) return { ok: false, error: "IBAN invalide." };
      value = v;
    } else if (spec.kind === "bic") {
      value = text.replace(/\s+/g, "").toUpperCase();
      if (!/^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/.test(value)) return { ok: false, error: "BIC invalide." };
    } else if (spec.kind === "otp") {
      const v = normalizeTotp(text);
      if (!v) return { ok: false, error: "Clé de double authentification invalide (une clé en base32 ou une adresse otpauth://)." };
      value = v;
    }
    // A login's identifier holding an « @ » is an e-mail address (it fills either kind of field).
    const kind: FieldKind = spec.kind === "username" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "email" : spec.kind;
    fields.push({ kind, value });
  }
  if (input.kind === "identity" && !fields.length) return { ok: false, error: "Remplis au moins un champ." };
  const typed = typeof input.label === "string" ? input.label.trim().replace(/\s+/g, " ").slice(0, 60) : "";
  const card = fields.find((f) => f.kind === "card_number")?.value;
  const iban = fields.find((f) => f.kind === "iban")?.value;
  const first = fields.find((f) => f.kind === "first_name")?.value;
  const fallback =
    input.kind === "login"
      ? (hostLabel(website) ?? "Identifiant")
      : input.kind === "card"
        ? `•••• ${card!.slice(-4)}`
        : input.kind === "bank"
          ? `IBAN ${iban!.slice(0, 4)}…${iban!.slice(-4)}`
          : input.kind === "identity"
            ? first
              ? `de ${first}`
              : "principale"
            : "";
  const label = typed || fallback;
  if (!label) return { ok: false, error: "Donne un titre à cette note." };
  if (input.kind === "login" && !website) return { ok: false, error: "Indique le site de cet identifiant (par exemple https://www.netflix.com)." };
  return { ok: true, item: { kind: input.kind, label, website, fields } };
}

/** A masked preview of a value, for the owner's list (never the whole value; nothing for the model). */
export function hintFor(kind: FieldKind, value: string): string {
  const v = value.trim();
  switch (kind) {
    case "card_number":
      return `•••• ${v.replace(/\D/g, "").slice(-4)}`;
    case "iban": {
      const s = v.replace(/\s+/g, "");
      return `${s.slice(0, 4)} •••• ${s.slice(-4)}`;
    }
    case "email":
    case "username": {
      const at = v.indexOf("@");
      if (at > 0) return `${v.slice(0, Math.min(2, at))}•••${v.slice(at)}`;
      return v.length <= 2 ? "•••" : `${v.slice(0, 2)}•••`;
    }
    case "card_holder":
    case "bank_holder":
    case "full_name":
    case "first_name":
    case "last_name":
    case "city":
    case "country":
      return v.length <= 1 ? "•••" : `${v.slice(0, 1)}•••`;
    case "phone":
      return `•••• ${v.replace(/\D/g, "").slice(-2)}`;
    case "card_expiry":
      return "••/••";
    case "otp":
      return "clé enregistrée";
    default:
      return "••••••••";
  }
}

/** The generated passwords' length and alphabets (no look-alike characters). */
export const GENERATED_PASSWORD_LENGTH = 24;
const PW_SETS = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnopqrstuvwxyz", "23456789", "-_.!@#%+=?"] as const;

/** Uniform index below `n` (rejection sampling, no modulo bias). */
function randomBelow(n: number): number {
  const limit = Math.floor(0x1_0000_0000 / n) * n;
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    if (buf[0]! < limit) return buf[0]! % n;
  }
}

/**
 * A strong password for an account the agent creates (upper and lower case, digits and symbols, each at least once):
 * generated in the Worker, sealed at once, typed by the fill helper — never seen by any model.
 */
export function generatePassword(length = GENERATED_PASSWORD_LENGTH): string {
  const all = PW_SETS.join("");
  const chars = PW_SETS.map((set) => set[randomBelow(set.length)]!);
  while (chars.length < length) chars.push(all[randomBelow(all.length)]!);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomBelow(i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join("");
}

/**
 * Field kinds a fill types when the page has them, never reported missing: the current two-factor code (only on the
 * code's page), a card's holder and billing address (not every checkout asks for them).
 */
export const OPTIONAL_FILL: ReadonlySet<FieldKind> = new Set<FieldKind>(["otp", "card_holder", "address", "city", "postal_code", "country"]);
